import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import { Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Slider from '@mui/material/Slider';
import { Column } from '@tanstack/react-table';
import { SyntheticEvent, useState } from 'react';
import { Input } from '../../Form';
import Popper from '../../Popper';
import Translate from '../../Translate';
import type { ColumnFilterProps, FilterLayout } from '../types';
import { FilterPopperPaper } from './FilterPopperPaper';
import { getInputProps } from './util';

function getFilterValue(column: Column<any>): number[] | undefined {
  const filterValue = column.getFilterValue();
  if (Array.isArray(filterValue)) {
    return filterValue;
  }

  const minMax = getMinMax(column);
  if (typeof minMax.at(0) === 'number' && typeof minMax.at(1) === 'number') {
    return minMax;
  }

  return undefined;
}

function getDisplayValue(column: Column<any>): string {
  const filterValue = column.getFilterValue();

  return Array.isArray(filterValue)
    ? `${filterValue.at(0)} ... ${filterValue.at(1)}`
    : '';
}

interface NumberFilterPopperContentProps extends PopperContentProps {
  column: Column<any>;
}

function getMinMax(column: Column<any>): [number, number] {
  // Using getFacetedUniqueValues() as getFacetedMinMaxValues() at times returns unexpected results. For example, [[-20], 300].
  const facetedUniqueValues = column.getFacetedUniqueValues();
  const values: number[] = [...facetedUniqueValues.keys()].map((v) =>
    Number.isFinite(v) ? v : 0
  );

  return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))];
}

const PopperContent = ({ column }: NumberFilterPopperContentProps) => {
  const texts = useTexts().component.table;
  const [min, max] = getMinMax(column);
  const [filterValue] = useState(getFilterValue(column));
  const { step = 1 } = column.columnDef.meta ?? {};

  const handleChange = (
    event: Event | SyntheticEvent,
    newValue: number | number[]
  ) => {
    column.setFilterValue(newValue);
  };

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return (
      <FilterPopperPaper>
        <Typography variant="body2">
          <Translate text={texts.filter.number.disabled} />
        </Typography>
      </FilterPopperPaper>
    );
  }

  return (
    <FilterPopperPaper sx={{ px: 4, pt: 6, pb: 1, width: 300 }}>
      <Slider
        defaultValue={filterValue}
        onChangeCommitted={handleChange}
        valueLabelDisplay="on"
        min={min}
        max={max}
        step={step}
        size="small"
      />
    </FilterPopperPaper>
  );
};

interface NumberFilterPopperTriggerProps extends PopperTriggerProps {
  column: Column<any>;
  layout: FilterLayout;
}

const PopperTrigger = ({
  onClick,
  column,
  layout,
}: NumberFilterPopperTriggerProps) => {
  const texts = useTexts();
  const filterValue = column.getFilterValue();
  const hasFilter = Array.isArray(filterValue);
  const displayValue = getDisplayValue(column);

  return (
    <Input
      value={displayValue}
      name={column.id}
      onClick={onClick}
      InputProps={{
        endAdornment: hasFilter && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                column.setFilterValue(undefined);
              }}
            >
              <Icon name="close" size="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...getInputProps(column, layout, texts)}
    />
  );
};

export const NumberColumnFilter = ({ column, layout }: ColumnFilterProps) => (
  <Popper
    Content={PopperContent}
    Trigger={PopperTrigger}
    triggerProps={{ column, layout }}
    contentProps={{ column }}
  />
);
